import { useCallback, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { cn } from '@/utils/cn'
import { setOnboardingReturnTo } from '@/utils/onboardingReturn'

/**
 * The visual language for data a `browse`-tier session was never sent.
 *
 * 🔴 READ THIS BEFORE USING ANY OF IT.
 *
 * A CSS blur laid over a real value is NOT a gate. One devtools click removes
 * it, and it is worse than nothing because it looks secure. Everything in this
 * file renders a placeholder from the ABSENCE of a value — the caller must have
 * chosen a data source that never carried the number in the first place
 * (`GET /api/v1/orderbook/public`), or skipped the request entirely.
 *
 * The rule for every call site: if you can see the real number in the props you
 * are passing here, you have gated the wrong layer. Go up to the fetch.
 *
 * The bars below are drawn from a repeated block glyph, deliberately NOT from
 * digits or currency symbols. A blurred "$12.4M" reads as a real figure someone
 * merely hid; a blurred block reads as "there is nothing here for you yet",
 * which is the truth. The glyphs are `aria-hidden` and every element carries a
 * real accessible label instead, so nothing leaks through the accessibility
 * tree — and there is nothing to leak, because no value was fetched.
 */

/** The affordance every gated surface routes into. */
export const useEnterPhaseTwo = () => {
  const navigate = useNavigate()
  return useCallback(
    (returnTo?: string) => {
      setOnboardingReturnTo(returnTo ?? window.location.pathname + window.location.search)
      navigate('/attestation')
    },
    [navigate],
  )
}

interface GatedValueProps {
  /**
   * Roughly how wide the real value will be, in `ch` units, so the blurred bar
   * reserves the same space and nothing on the page moves when the user attests
   * and real numbers arrive. `$12.3M` is about 6.
   */
  widthCh?: number
  /** What is hidden, for the accessible name. E.g. "fully diluted valuation". */
  label: string
  className?: string
}

/**
 * An inline blurred bar sized to the value it stands in for.
 *
 * Used in table cells and stat tiles. Not interactive itself — a table row is
 * already clickable and nesting a button inside it breaks the row's hit target.
 * The surrounding surface provides the route into phase 2.
 */
export const GatedValue = ({ widthCh = 6, label, className }: GatedValueProps) => (
  <span
    className={cn('inline-flex items-center align-middle', className)}
    role="img"
    aria-label={`${label} hidden until verification is complete`}
    title="Complete verification to see this"
  >
    {/*
      A plain bar in `currentColor`, sized in `ch` and `em` so it scales with
      whatever type it sits in — a 14px table cell or a 28px stat tile — and
      lines up with the value it replaces.

      Not blurred DIGITS. A blurred "$12.4M" reads as a real figure someone
      merely covered up, and invites both a devtools poke and a wrong guess
      about the market. A bar reads as "nothing here for you yet", which is
      what is true: no number was fetched.
    */}
    <span
      aria-hidden="true"
      className="inline-block select-none rounded-[2px] bg-current opacity-[0.28]"
      style={{ width: `${widthCh}ch`, height: '0.72em', filter: 'blur(3px)' }}
    />
  </span>
)

interface GatedNoticeProps {
  /** What is missing, e.g. "Buy / sell split". */
  title: string
  description: string
  /**
   * Whether the affordance is offered. False for a transient failure — a
   * "continue verification" button on a 500 sends the user somewhere that
   * cannot help them.
   */
  actionable?: boolean
  className?: string
}

/**
 * The one-line version, for a slot too small for `GatedPanel`.
 *
 * Used where a locked figure sits inside a fixed-height card and a full panel
 * would overflow it.
 */
export const GatedNotice = ({
  title,
  description,
  actionable = true,
  className,
}: GatedNoticeProps) => {
  const enterPhaseTwo = useEnterPhaseTwo()

  return (
    <div className={cn('flex h-full items-center gap-3', className)}>
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[rgba(253,253,253,0.1)]">
        <Lock size={13} className="text-[#2d2923] dark:text-[#fdfdfd]" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-plus-jakarta text-[13px] font-semibold leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
          {title}
        </p>
        <p className="font-plus-jakarta text-[12px] font-normal leading-[16px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
          {description}
        </p>
      </div>
      {actionable && (
        <button
          type="button"
          onClick={() => enterPhaseTwo()}
          className={
            'shrink-0 font-plus-jakarta text-[12px] font-medium underline underline-offset-2 ' +
            'text-[#2d2923] dark:text-[#fdfdfd]'
          }
        >
          Verify
        </button>
      )}
    </div>
  )
}

interface GatedPanelProps {
  /** Names what the panel would show, e.g. "Market indicator". */
  title: string
  /** One line on why it is hidden and what unlocks it. */
  description?: string
  /**
   * Reserve the real panel's height so the dashboard does not reflow when the
   * user attests. Pass the same class the real panel uses (e.g. `h-[336px]`).
   */
  className?: string
  /** Where to send the user back to after phase 2. Defaults to the current URL. */
  returnTo?: string
  children?: ReactNode
}

/**
 * A block-level stand-in for a whole panel whose data was never requested.
 *
 * The blurred bars behind the message are decoration, not hidden content: this
 * component is rendered INSTEAD of the panel, and the panel's props were never
 * populated.
 */
export const GatedPanel = ({
  title,
  description,
  className,
  returnTo,
  children,
}: GatedPanelProps) => {
  const enterPhaseTwo = useEnterPhaseTwo()

  return (
    <div
      className={cn(
        'relative flex w-full flex-col items-center justify-center gap-3 overflow-hidden px-6 py-8 text-center',
        className,
      )}
    >
      {/* Decorative blurred bars. No data reaches this component. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex flex-col justify-center gap-3 px-8 opacity-40">
        {[68, 84, 52, 76].map((width, index) => (
          <div
            key={index}
            className="h-4 bg-[rgba(45,41,35,0.22)] dark:bg-[rgba(253,253,253,0.22)]"
            style={{ width: `${width}%`, filter: 'blur(6px)' }}
          />
        ))}
      </div>

      <div className="relative z-10 flex flex-col items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[rgba(253,253,253,0.1)]">
          <Lock size={16} className="text-[#2d2923] dark:text-[#fdfdfd]" />
        </span>
        <p className="font-plus-jakarta text-[15px] font-semibold leading-[22px] text-[#2d2923] dark:text-[#fdfdfd]">
          {title}
        </p>
        <p className="max-w-[320px] font-plus-jakarta text-[13px] font-normal leading-[18px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
          {description ?? 'Finish verifying your institution to see pricing and valuations.'}
        </p>
        {children}
        <button
          type="button"
          onClick={() => enterPhaseTwo(returnTo)}
          className={
            'mt-1 h-9 border border-[#2d2923] px-4 font-plus-jakarta text-[13px] font-medium ' +
            'text-[#2d2923] transition-colors hover:bg-[#2d2923] hover:text-white ' +
            'dark:border-[#fdfdfd] dark:text-[#fdfdfd] dark:hover:bg-[#fdfdfd] dark:hover:text-[#2d2923]'
          }
        >
          Continue verification
        </button>
      </div>
    </div>
  )
}

/**
 * The banner that tells a `browse` user, once, why the page looks like this.
 *
 * One explanation at the top beats repeating the same sentence inside six
 * panels.
 */
export const BrowseModeBanner = () => {
  const enterPhaseTwo = useEnterPhaseTwo()

  return (
    <div
      className={
        'flex flex-col gap-3 border border-[rgba(45,41,35,0.12)] bg-[rgba(255,255,255,0.62)] px-4 py-3 ' +
        'dark:border-[rgba(253,253,253,0.12)] dark:bg-[rgba(28,28,28,0.7)] sm:flex-row sm:items-center sm:justify-between'
      }
    >
      <div className="flex items-start gap-3">
        <Lock size={16} className="mt-[3px] shrink-0 text-[#2d2923] dark:text-[#fdfdfd]" />
        <div>
          <p className="font-plus-jakarta text-[14px] font-semibold leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
            You are browsing SecondLane
          </p>
          <p className="font-plus-jakarta text-[13px] font-normal leading-[18px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
            Pricing, valuations and trading open up once your institution is verified.
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => enterPhaseTwo()}
        className={
          'h-10 shrink-0 bg-[#2d2923] px-4 font-plus-jakarta text-[13px] font-medium text-white ' +
          'transition-opacity hover:opacity-90 dark:bg-[#fdfdfd] dark:text-[#2d2923]'
        }
      >
        Continue verification
      </button>
    </div>
  )
}
